import { useEffect, useRef, useState } from 'react';
import {
  downloadOfflineRecovery,
  recoveryText,
} from '../../utils/mindmap/offlineRecovery';
import './OfflineRecoveryDialog.css';

export default function OfflineRecoveryDialog({ record, onReload, onDismiss }) {
  const [copyStatus, setCopyStatus] = useState(null);
  const [downloaded, setDownloaded] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const dialogRef = useRef(null);
  const isOpen = !!record;

  useEffect(() => {
    setCopyStatus(null);
    setDownloaded(false);
    setIsReloading(false);
  }, [record]);

  useEffect(() => {
    if (!record) return undefined;
    const blockBackgroundShortcut = (event) => {
      if (dialogRef.current?.contains(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    window.addEventListener('keydown', blockBackgroundShortcut, true);
    return () => window.removeEventListener('keydown', blockBackgroundShortcut, true);
  }, [record]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    const frame = requestAnimationFrame(() => dialogRef.current?.focus());
    return () => {
      cancelAnimationFrame(frame);
      previousFocus?.focus?.();
    };
  }, [isOpen]);

  if (!record) return null;
  const accessRejected = record.kind === 'access-rejected';
  const conflictCount = record.conflicts?.length || 0;
  const hydrationPending = record.hydrationPending === true;
  const persistencePending = (
    record.persistenceStatus === 'saving'
    || record.persistenceStatus === 'pending-hydration'
  );
  const requiresDownload = (
    record.storageEligible === false || record.persistenceStatus === 'failed'
  );

  const downloadRecovery = () => {
    if (hydrationPending) return false;
    let started = false;
    try {
      started = downloadOfflineRecovery(record);
    } catch {
      started = false;
    }
    if (started) {
      setDownloaded(true);
      setCopyStatus('Recovery download started.');
    } else {
      setCopyStatus('The browser could not start the recovery download. Please try again.');
    }
    return started;
  };

  const copyText = async () => {
    const text = recoveryText(record);
    if (!text) {
      setCopyStatus('There is no text to copy from this recovery.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('Recovery text copied.');
    } catch {
      setCopyStatus('Copy was blocked by the browser. Download the JSON recovery instead.');
    }
  };

  return (
    <div className="offline-recovery-backdrop" role="presentation" data-export-ignore="true">
      <section
        ref={dialogRef}
        className="offline-recovery-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="offline-recovery-title"
        tabIndex="-1"
        onKeyDown={(event) => {
          if (event.key === 'Tab') {
            const controls = [...event.currentTarget.querySelectorAll(
              'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
            )];
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (!first) {
              event.preventDefault();
            } else if (event.shiftKey && (
              document.activeElement === first || document.activeElement === event.currentTarget
            )) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          }
          event.stopPropagation();
        }}
      >
        <div className="offline-recovery-dialog__symbol" aria-hidden="true">↺</div>
        <h2 id="offline-recovery-title">
          {accessRejected ? 'Your access to this map changed' : 'One of your offline edits needs review'}
        </h2>
        <p>
          {hydrationPending
            ? 'Mindmappi stopped syncing. Your full offline map is still loading from this device; the original offline copy will remain untouched until recovery is ready.'
            : accessRejected
            ? 'Mindmappi stopped syncing before accepting more changes. Your local map has been preserved as a recovery copy on this device.'
            : `${conflictCount} local idea ${conflictCount === 1 ? 'version was' : 'versions were'} replaced when collaborators’ changes converged. The accepted map is safe, and your wording is preserved below.`}
        </p>
        {record.storageEligible === false && (
          <p className="offline-recovery-dialog__warning">
            This map is too large for the bounded recovery store. Download it before leaving this page.
          </p>
        )}
        {record.persistenceStatus === 'failed' && (
          <p className="offline-recovery-dialog__warning">
            The browser could not save a durable recovery copy. Download it before continuing.
          </p>
        )}
        {record.persistenceStatus === 'saving' && (
          <p role="status">Saving the recovery copy on this device…</p>
        )}
        {copyStatus && <p role="status">{copyStatus}</p>}
        <div className="offline-recovery-dialog__actions">
          <button type="button" onClick={downloadRecovery} disabled={hydrationPending}>
            Download recovery JSON
          </button>
          <button type="button" onClick={copyText} disabled={hydrationPending}>
            Copy recovered text
          </button>
          {accessRejected ? (
            <button
              type="button"
              className="offline-recovery-dialog__secondary"
              disabled={hydrationPending || isReloading}
              onClick={async () => {
                if (!downloadRecovery()) return;
                setIsReloading(true);
                const ready = await onReload?.();
                if (ready === false) {
                  setIsReloading(false);
                  setCopyStatus(
                    'Mindmappi could not clear the rejected offline sync cache. Your recovery is downloaded; please retry before reloading.'
                  );
                }
              }}
            >
              {isReloading ? 'Preparing safe reload…' : 'Download recovery and reload'}
            </button>
          ) : (
            <button
              type="button"
              className="offline-recovery-dialog__secondary"
              disabled={
                hydrationPending
                || persistencePending
                || isReloading
                || (requiresDownload && !downloaded)
              }
              onClick={async () => {
                setIsReloading(true);
                try {
                  const accepted = await onDismiss?.();
                  if (accepted === false) setCopyStatus('The browser could not remember your choice. Please retry; your recovery copy is still safe.');
                } catch {
                  setCopyStatus('The browser could not remember your choice. Please retry; your recovery copy is still safe.');
                } finally {
                  setIsReloading(false);
                }
              }}
            >
              Continue with accepted map
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
